import { Container, Graphics, Text } from "pixi.js";

import type { ShotHit } from "../../../shared/ArenaState";
import { wrapDelta } from "../../../shared/Sailing";

/**
 * Cream, the palette's "this came off a hull" colour — the same one a turbo trail is drawn in
 * (design/UI.md). Not the white of the sea's standing loot: these coins were never in the
 * water, and a burst of them crossing a field of white ones has to read as a different event
 * at a glance. Not the action red either — that is the number rising off the splash, and the
 * point of the coins is that they are the *loot*, not the label.
 */
const COLOR = 0xf2e2c4;
/**
 * A touch smaller than a coin in the sea (11 / 19 in LootField) so that thirty of them arriving
 * at once do not blot out the hull they are landing on, but plainly the same object.
 */
const RADIUS = 8;
const HALO_RADIUS = 14;
const HALO_ALPHA = 0.2;

/** How long one coin is in the air, in seconds, and how much that varies from coin to coin. */
const FLIGHT_SECONDS = 0.62;
const FLIGHT_JITTER = 0.14;
/** Longest a coin waits on the deck before it leaves, in seconds. See `open`. */
const RELEASE_DELAY = 0.09;
/**
 * The shape of the flight, as two numbers: the **fraction of the life** spent breaking away
 * from the victim, and **how far along the path** that break gets.
 *
 * A third of the time to cover a quarter of the distance is what makes the event read as two
 * things rather than one — the hit knocks the coins loose, and then the attacker takes them. A
 * straight lerp from hull to hull is a stream of dots and says nothing about which end is the
 * thief.
 */
const RELEASE_FRACTION = 0.34;
const RELEASE_REACH = 0.26;
/** How far out a coin is flung on the break, in world pixels — roughly half a hull. */
const BURST_MIN = 55;
const BURST_MAX = 140;
/** The last part of the flight, over which a coin shrinks and fades into the deck it lands on. */
const LAND_FRACTION = 0.16;

/** Fewest and most coins one hit throws, and the scale between. See `coinCount`. */
const COINS_MIN = 9;
const COINS_MAX = 30;
const COINS_PER_POINT = 2;

/** The streak behind a coin: shortest step worth drawing one for, and how heavy it is. */
const TAIL_MIN_STEP = 7;
const TAIL_WIDTH = 0.55;
const TAIL_ALPHA = 0.4;

/**
 * The reward label — the `+120` that lands on the attacker's hull. Its life in seconds, and
 * the fraction of that life it holds at full opacity before it starts to go.
 *
 * Long enough to be read at a glance in the middle of a fight and short enough that a rolling
 * broadside does not leave a column of numbers standing over the boat.
 */
const LABEL_SECONDS = 1.1;
const LABEL_HOLD = 0.55;
const LABEL_SIZE = 22;
/**
 * The pop: it overshoots its size and settles back, which is what makes the number arrive
 * rather than appear. Both in seconds, and the overshoot as a multiple of the resting scale.
 */
const LABEL_POP_SECONDS = 0.2;
const LABEL_SETTLE_SECONDS = 0.14;
const LABEL_POP_SCALE = 1.15;
/**
 * How far it drifts up over its life, in world pixels. Short: the rise is there to lift the
 * number clear of the deck and give it somewhere to fade, not to carry it off the boat. A
 * number that ends up half a screen from the hull has stopped saying *whose* it is.
 */
const LABEL_RISE = 34;
/**
 * Where it starts, as a fraction of that hull's length above its centre — the same unit the
 * nameplate under the boat is dropped in, so a Galleon's number clears a Galleon.
 */
const LABEL_LIFT_FACTOR = 0.55;
/**
 * How far **below** the last one a number starts when it lands while an earlier one is still
 * up, in world pixels.
 *
 * Being hit twice in a row is a designed part of this game (design/Battle.md), so two payouts
 * inside a second is the normal case rather than the odd one, and two numbers on the same spot
 * is one unreadable number.
 *
 * Downward, and that is the whole trick: an older label has already been rising, so the new one
 * belongs in the space underneath it. Stacking *upward* — the obvious reading of "get out of the
 * way" — walks the new number straight into the old one, because it starts higher and is then
 * lifted by the same rise. Sailing under the stack lets the two separate on their own.
 */
const LABEL_STACK = 26;
/** Deep teal, as everywhere else a number is drawn over the water. */
const LABEL_STROKE = 0x0b2a35;

/** How far off screen a coin is still drawn, in pixels. Covers the halo at any position. */
const CULL_MARGIN = HALO_RADIUS * 2;
/** The same for a label, which is wider than it is tall and sits above the hull it belongs to. */
const LABEL_CULL_MARGIN = 120;

/** One coin, in pixels **offset from the hit** rather than in world coordinates. See `update`. */
interface Coin {
    /** The control point of its arc: where the break flings it, relative to the hit. */
    cx: number;
    cy: number;
    /** Where it was drawn last frame, for the streak. The same offset space. */
    px: number;
    py: number;
    /** Seconds it waits before leaving, seconds it then spends in the air, and its age. */
    delay: number;
    life: number;
    age: number;
}

/** One hit's worth of coins, all of them heading for the same hull. */
interface Burst {
    /** Where the hit landed, in world pixels. Every coin's position is measured from here. */
    x: number;
    y: number;
    /** The attacker, as a `sessionId`: whose hull the coins are being pulled onto. */
    to: string;
    /** Where that hull is, as an offset from the hit. Re-read every frame — the target sails. */
    tx: number;
    ty: number;
    coins: Coin[];
    /** What the hit was worth. Held only to put a number on the label when the coins land. */
    plunder: number;
    /** Seconds until the first coin arrives, this burst's age, and whether it has paid out. */
    payAt: number;
    age: number;
    paid: boolean;
}

/** A `+120` riding an attacker's hull. */
interface Reward {
    /** Whose hull it is riding, and where that hull was when this was last able to ask. */
    to: string;
    x: number;
    y: number;
    /** How far above the hull's centre it floats, in world pixels. Fixed when it lands. */
    lift: number;
    age: number;
    label: Text;
}

/** A ship as this field needs it: where it is being drawn, and how big it is. */
export interface Hull {
    /** World position, in pixels. */
    x: number;
    y: number;
    /** Hull length in world pixels, which is what the label is lifted clear of. */
    length: number;
}

/**
 * The plunder, in flight: the coins a hit knocks off one hull and drags onto another, and the
 * number that lands on the deck when they arrive.
 *
 * **None of it exists on the server.** A hit moves points between two `score` fields in a single
 * step and still does (design/Battle.md); what this draws is that instant, spread over half a
 * second so a player can see *where their points went*. Nothing here is collectable, nothing
 * here is in `ArenaState`, and a client that dropped every frame of it would be playing exactly
 * the same game — which is the licence that lets it be generous with the count.
 *
 * The motion is one quadratic curve per coin: from the hit, through the point the break flings
 * it to, to the attacker — with that last point **re-read every frame**, so the coins chase a
 * hull that is still sailing instead of piling up where it was when the ball landed. Eased
 * slow-then-fast (see RELEASE_FRACTION), it gives the event the two beats it actually has: the
 * spill, and the theft.
 *
 * **The number is the third beat, and it is deliberately last.** It appears when the first coin
 * touches the hull rather than when the ball does — so the payout reads as the *consequence* of
 * the shower arriving, not as a caption on it. The victim's loss is already on screen by then,
 * in the action red over the splash (`ShotField`); this is the other half of the same transfer,
 * in the cream the coins are drawn in, over the hull that took it.
 *
 * Like the loot and the shots it sits **above** the water rather than inside it — the
 * displacement filter would drag the coins away from the two hulls the whole gesture is about.
 * Above the hulls too, unlike the loot: a coin landing on a deck must not vanish behind it.
 */
export class PlunderField {
    private readonly _container = new Container();
    private readonly _tails = new Graphics();
    private readonly _coins = new Graphics();
    private readonly _labels = new Container();

    private readonly _bursts: Burst[] = [];
    private readonly _rewards: Reward[] = [];
    /** Retired labels, kept for the next payout rather than destroyed and remade. */
    private readonly _labelPool: Text[] = [];

    private _width = 0;
    private _height = 0;

    public get container(): Container {
        return this._container;
    }

    constructor() {
        // Streaks under the coins: a tail is the ghost of where one has been, and should never
        // be drawn over the one in front of it. The numbers over both — a payout is the point of
        // the whole gesture and must not be hidden by the coins that earned it.
        this._container.addChild(this._tails, this._coins, this._labels);
    }

    /**
     * Knocks a hit's worth of coins loose. Called off the same `hit` message the splash is.
     *
     * The launch angles are **spread evenly around the circle** with a fraction of a segment of
     * jitter, rather than each being rolled loose: a handful of independent random bearings
     * clumps, and a clumped burst reads as a few coins with a bare side rather than as a hull
     * losing its hold on them.
     */
    public open(hit: ShotHit): void {
        const count = coinCount(hit.plunder);
        const coins: Coin[] = [];
        let payAt = Number.POSITIVE_INFINITY;

        for (let i = 0; i < count; i++) {
            const angle = ((i + Math.random()) / count) * Math.PI * 2;
            const reach = BURST_MIN + Math.random() * (BURST_MAX - BURST_MIN);
            const delay = Math.random() * RELEASE_DELAY;
            const life = FLIGHT_SECONDS * (1 + (Math.random() * 2 - 1) * FLIGHT_JITTER);

            coins.push({ cx: Math.sin(angle) * reach, cy: -Math.cos(angle) * reach, px: 0, py: 0, delay, life, age: 0 });

            // The label lands with the *first* coin, so it is derived from the flights rather
            // than being a fourth timing constant that would quietly drift out of step with
            // them the next time FLIGHT_SECONDS is touched.
            payAt = Math.min(payAt, delay + life);
        }

        this._bursts.push({ x: hit.x, y: hit.y, to: hit.from, tx: 0, ty: 0, coins, plunder: hit.plunder, payAt, age: 0, paid: false });
    }

    /**
     * Flies every coin one frame, pays out any burst that has arrived, and redraws the lot
     * against the camera.
     *
     * `locate` answers where a ship is **being drawn** — the predicted position for the local
     * hull, the smoothed one for everyone else — rather than where the server last said it was,
     * because the coins have to land on the boat the player can see. It may answer `null`: a
     * hull that left the arena mid-flight keeps the last offset it had, so the coins finish
     * their arc into empty water rather than snapping back to the hit.
     *
     * Everything is worked in **offsets from the hit**, which is what keeps the torus out of it:
     * one `wrapDelta` to find the attacker, one to find the camera, and the curve in between is
     * plain arithmetic that cannot fly the long way round the arena. See design/Networking.md.
     */
    public update(locate: (sessionId: string) => Hull | null, cameraX: number, cameraY: number, deltaSeconds: number): void {
        this._tails.clear();
        this._coins.clear();

        const centreX = this._width / 2;
        const centreY = this._height / 2;

        for (let b = this._bursts.length - 1; b >= 0; b--) {
            const burst = this._bursts[b];
            burst.age += deltaSeconds;

            const ship = locate(burst.to);
            if (ship) {
                burst.tx = wrapDelta(burst.x, ship.x);
                burst.ty = wrapDelta(burst.y, ship.y);
            }

            // The moment the shower reaches the deck. Paid once, and only if there is still a
            // hull to pay it onto — a number floating over open water where a ship used to be
            // says nothing to anybody.
            if (!burst.paid && burst.age >= burst.payAt) {
                burst.paid = true;
                if (ship) this._pay(burst, ship);
            }

            const originX = centreX + wrapDelta(cameraX, burst.x);
            const originY = centreY + wrapDelta(cameraY, burst.y);

            let live = 0;

            for (const coin of burst.coins) {
                coin.age += deltaSeconds;

                const life = (coin.age - coin.delay) / coin.life;
                if (life >= 1) continue;

                live++;
                // Knocked loose, but not away yet: this is the stagger RELEASE_DELAY buys.
                if (life <= 0) continue;

                const u = along(life);
                const k = 1 - u;
                // Quadratic Bézier with the hit at the origin, so its first term drops out.
                const x = 2 * k * u * coin.cx + u * u * burst.tx;
                const y = 2 * k * u * coin.cy + u * u * burst.ty;

                this._draw(originX + x, originY + y, originX + coin.px, originY + coin.py, life);

                coin.px = x;
                coin.py = y;
            }

            if (live === 0) this._bursts.splice(b, 1);
        }

        this._carryLabels(locate, cameraX, cameraY, deltaSeconds);
    }

    public resize(width: number, height: number): void {
        this._width = width;
        this._height = height;
    }

    public destroy(): void {
        this._bursts.length = 0;
        this._rewards.length = 0;
        this._labelPool.length = 0;
        this._container.destroy({ children: true });
    }

    /** Puts the payout on the attacker's deck, clear of any number already standing there. */
    private _pay(burst: Burst, ship: Hull): void {
        let stacked = 0;
        for (const reward of this._rewards) {
            if (reward.to === burst.to) stacked++;
        }

        const label = this._takeLabel();
        label.text = `+${burst.plunder}`;

        this._rewards.push({
            to: burst.to,
            x: ship.x,
            y: ship.y,
            lift: ship.length * LABEL_LIFT_FACTOR - stacked * LABEL_STACK,
            age: 0,
            label
        });
    }

    /**
     * Drifts every live payout up its hull and fades it out.
     *
     * The hull is asked for again each frame rather than the number being dropped at the spot
     * the coins landed on: a ship under way covers most of a hull length while its number is on
     * screen, and a `+120` left behind in the water belongs to nobody. A hull that leaves
     * mid-label keeps the last position it had and fades out there.
     */
    private _carryLabels(locate: (sessionId: string) => Hull | null, cameraX: number, cameraY: number, deltaSeconds: number): void {
        const centreX = this._width / 2;
        const centreY = this._height / 2;

        for (let i = this._rewards.length - 1; i >= 0; i--) {
            const reward = this._rewards[i];
            reward.age += deltaSeconds;

            if (reward.age >= LABEL_SECONDS) {
                this._releaseLabel(reward.label);
                this._rewards.splice(i, 1);
                continue;
            }

            const ship = locate(reward.to);
            if (ship) {
                reward.x = ship.x;
                reward.y = ship.y;
            }

            const life = reward.age / LABEL_SECONDS;
            const x = centreX + wrapDelta(cameraX, reward.x);
            // Eased out, so it leaps off the deck and then hangs: the rise is there to get the
            // number clear of the hull, not to walk it off the top of the screen.
            const y = centreY + wrapDelta(cameraY, reward.y) - reward.lift - LABEL_RISE * (1 - (1 - life) * (1 - life));

            const onScreen =
                x > -LABEL_CULL_MARGIN &&
                x < this._width + LABEL_CULL_MARGIN &&
                y > -LABEL_CULL_MARGIN &&
                y < this._height + LABEL_CULL_MARGIN;

            reward.label.visible = onScreen;
            if (!onScreen) continue;

            reward.label.position.set(x, y);
            reward.label.scale.set(popScale(reward.age));
            reward.label.alpha = life < LABEL_HOLD ? 1 : 1 - ((life - LABEL_HOLD) / (1 - LABEL_HOLD)) ** 2;
        }
    }

    /** One coin and its streak, at screen positions, `life` of the way through its flight. */
    private _draw(x: number, y: number, fromX: number, fromY: number, life: number): void {
        if (x < -CULL_MARGIN || x > this._width + CULL_MARGIN) return;
        if (y < -CULL_MARGIN || y > this._height + CULL_MARGIN) return;

        // Full size for most of the trip, then swallowed by the hull it arrives at.
        const landing = Math.max(0, (life - (1 - LAND_FRACTION)) / LAND_FRACTION);
        const scale = 1 - landing;
        const alpha = 1 - landing * landing;
        const radius = RADIUS * scale;

        const stepX = x - fromX;
        const stepY = y - fromY;

        // Only once it is really moving. At the top of the arc the step is a pixel or two, and
        // a streak there would draw a smear on a coin that is very nearly hanging still.
        if (Math.abs(stepX) + Math.abs(stepY) > TAIL_MIN_STEP) {
            this._tails
                .moveTo(fromX, fromY)
                .lineTo(x, y)
                .stroke({ color: COLOR, width: radius * TAIL_WIDTH * 2, alpha: TAIL_ALPHA * alpha, cap: "round" });
        }

        this._coins.circle(x, y, HALO_RADIUS * scale).fill({ color: COLOR, alpha: HALO_ALPHA * alpha });
        this._coins.circle(x, y, radius).fill({ color: COLOR, alpha });
    }

    private _takeLabel(): Text {
        const pooled = this._labelPool.pop();
        if (pooled) {
            pooled.visible = true;
            return pooled;
        }

        const label = new Text({
            text: "",
            anchor: 0.5,
            style: {
                fontFamily: "Grobold",
                fontSize: LABEL_SIZE,
                fill: COLOR,
                stroke: { color: LABEL_STROKE, width: 4, join: "round" }
            }
        });
        this._labels.addChild(label);

        return label;
    }

    private _releaseLabel(label: Text): void {
        label.visible = false;
        this._labelPool.push(label);
    }
}

/**
 * How far along its path a coin is, `life` of the way through its flight.
 *
 * Two eased segments meeting at (RELEASE_FRACTION, RELEASE_REACH): the break decelerates out of
 * the hit, and the pull accelerates into the attacker. The join is deliberately not smooth — the
 * moment a coin stops drifting and starts being *taken* is the moment worth seeing.
 */
function along(life: number): number {
    if (life < RELEASE_FRACTION) {
        const t = life / RELEASE_FRACTION;
        return RELEASE_REACH * (1 - (1 - t) * (1 - t));
    }

    const t = (life - RELEASE_FRACTION) / (1 - RELEASE_FRACTION);
    return RELEASE_REACH + (1 - RELEASE_REACH) * t * t;
}

/**
 * The size of a payout label `age` seconds in: up past full size, then back onto it.
 *
 * The overshoot is the whole reason the number reads as *landing*. A label that faded in at its
 * resting size would be a caption that was always going to be there; one that arrives too big
 * and settles is something the coins just did.
 */
function popScale(age: number): number {
    if (age < LABEL_POP_SECONDS) {
        const t = age / LABEL_POP_SECONDS;
        return LABEL_POP_SCALE * (1 - (1 - t) * (1 - t));
    }

    const t = Math.min(1, (age - LABEL_POP_SECONDS) / LABEL_SETTLE_SECONDS);
    return LABEL_POP_SCALE + (1 - LABEL_POP_SCALE) * t;
}

/**
 * How many coins a hit throws, from what it was worth.
 *
 * The **square root** of the plunder rather than a share of it. A count that tracked the points
 * one for one would put four coins in the air for a hit on a fresh hull and four hundred for a
 * leader's hold — the first too thin to read as a theft, the second a wall of cream over the
 * fight it is meant to be describing. A root grows with the prize and then flattens: a big steal
 * is visibly a bigger shower, and never an unreadable one.
 *
 * It is a decision rather than a dial: it says nothing about the economy — the label the coins
 * land with is the number a player acts on — so there is nothing here to tune against.
 */
function coinCount(plunder: number): number {
    return Math.max(COINS_MIN, Math.min(COINS_MAX, Math.round(Math.sqrt(plunder * COINS_PER_POINT))));
}
